import React, { useEffect, useCallback, useRef } from 'react';
import {
  EQUITY_METHOD_CHANGE_MODAL_OPEN_EVENT,
  EQUITY_METHOD_CHANGE_MODAL_CLOSE_EVENT,
  getEquityMethodChangeModalContext,
  closeEquityMethodChangeModal
} from '../utils/equityMethodChangeModalBus';
import { supabase } from '../lib/supabase_client';

export const EquityMethodChangeModal: React.FC = () => {
  // Main modal refs
  const modalRootRef = useRef<HTMLDivElement>(null);
  const modalBackdropRef = useRef<HTMLDivElement>(null);
  const modalPanelRef = useRef<HTMLDivElement>(null);
  
  const currentMethodDisplayRef = useRef<HTMLDivElement>(null);
  const newMethodSelectRef = useRef<HTMLSelectElement>(null);
  
  const contextRef = useRef<any>(null);
  
  // Mounted guard
  const mountedRef = useRef(true);
  
  // Refs to track if secondary modals exist (singleton pattern)
  const warningModalRef = useRef<HTMLElement | null>(null);
  const processingModalRef = useRef<HTMLElement | null>(null);
  
  // Refs for event listeners cleanup
  const eventListenersRef = useRef<Array<{element: HTMLElement, event: string, handler: any}>>([]);

  // Helper to add event listener and track for cleanup
  const addTrackedListener = useCallback((element: HTMLElement | null, event: string, handler: any) => {
    if (!element || !mountedRef.current) return;
    
    element.addEventListener(event, handler);
    eventListenersRef.current.push({ element, event, handler });
  }, []);

  // Helper to clean up all tracked listeners
  const cleanupListeners = useCallback(() => {
    eventListenersRef.current.forEach(({ element, event, handler }) => {
      element?.removeEventListener(event, handler);
    });
    eventListenersRef.current = [];
  }, []);

  const showWarningModal = useCallback((newMethod: string) => {
    if (!mountedRef.current) return;

    // Check if modal already exists
    let warningModal = document.getElementById('equityWarningModal');
    
    if (!warningModal) {
      // Create modal only if it doesn't exist
      const warningHtml = `
        <div style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10001; padding: 16px;">
          <div style="background: white; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); max-width: 600px; width: 100%; overflow: hidden;">
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgb(226, 232, 240); padding: 10px 12px;">
              <div>
                <h3 style="font-size: 16px; font-weight: 600; color: var(--theme-400); margin: 0;">⚠️ Equity Method Change</h3>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: rgb(100, 116, 139);">Important reminder</p>
              </div>
              <button type="button" id="equityWarningClose" style="display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; border: none; background: transparent; color: rgb(100, 116, 139); cursor: pointer; transition: all 0.15s; font-size: 24px; line-height: 1;">×</button>
            </div>
            <div style="padding: 24px 20px;">
              <div style="font-size: 16px; margin-bottom: 20px; text-align: center;">
                <strong>Please save your current changes before proceeding with equity method conversion.</strong>
              </div>
              <div style="background: #fffbeb; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
                <strong style="display: block; margin-bottom: 8px; color: #78350f;">What happens next:</strong>
                <ul style="margin: 0; padding-left: 20px; color: #78350f; line-height: 1.6;">
                  <li>Equity method conversion will take around 2 minutes</li>
                  <li>You will be redirected to filings page after completion</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
              <div style="color: #dc2626; font-size: 14px; font-weight: 600; text-align: center;">
                Make sure to save your changes before proceeding!
              </div>
            </div>
            <div class="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
              <button type="button" id="equityWarningCancel" class="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400">
                Cancel
              </button>
              <button type="button" id="equityWarningProceed" class="inline-flex items-center justify-center rounded bg-slate-900 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400">
                I've Saved - Proceed
              </button>
            </div>
          </div>
        </div>
      `;

      const warningDiv = document.createElement('div');
      warningDiv.id = 'equityWarningModal';
      warningDiv.innerHTML = warningHtml;
      document.body.appendChild(warningDiv);
      
      warningModal = warningDiv;
      warningModalRef.current = warningDiv;
    }

    // Show modal
    (warningModal as HTMLElement).style.display = 'flex';

    // Attach event listeners (use setTimeout to ensure DOM is ready)
    requestAnimationFrame(() => {
      if (!mountedRef.current) return;

      const hideWarning = () => {
        if (warningModalRef.current) {
          warningModalRef.current.style.display = 'none';
        }
      };

      const closeBtn = document.getElementById('equityWarningClose');
      const cancelBtn = document.getElementById('equityWarningCancel');
      const proceedBtn = document.getElementById('equityWarningProceed');

      if (closeBtn) {
        const hoverIn = () => (closeBtn as HTMLElement).style.background = 'rgb(241, 245, 249)';
        const hoverOut = () => (closeBtn as HTMLElement).style.background = 'transparent';
        
        addTrackedListener(closeBtn, 'click', hideWarning);
        addTrackedListener(closeBtn, 'mouseenter', hoverIn);
        addTrackedListener(closeBtn, 'mouseleave', hoverOut);
      }
      
      if (cancelBtn) {
        addTrackedListener(cancelBtn, 'click', hideWarning);
      }
      
      if (proceedBtn) {
        addTrackedListener(proceedBtn, 'click', () => {
          hideWarning();
          proceedWithMethodChange(newMethod);
        });
      }
    });
  }, [mountedRef, addTrackedListener]);

  const showProcessingModal = useCallback(() => {
    if (!mountedRef.current) return;

    // Check if modal already exists
    let processingModal = document.getElementById('equityProcessingModal');
    
    if (!processingModal) {
      const processingHtml = `
        <div style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10002; padding: 16px;">
          <div style="background: white; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); max-width: 500px; width: 100%; overflow: hidden;">
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgb(226, 232, 240); padding: 10px 12px;">
              <div>
                <h3 style="font-size: 16px; font-weight: 600; color: var(--theme-400); margin: 0;">🔄 Processing Equity Method Change</h3>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: rgb(100, 116, 139);">Please wait</p>
              </div>
            </div>
            <div style="padding: 32px 24px; text-align: center;">
              <div style="font-size: 16px; margin-bottom: 20px; color: #374151; font-weight: 500;" id="equityProcessingStep">
                Converting equity statement structure...
              </div>
              <div style="background: #f3f4f6; border-radius: 10px; height: 8px; margin: 24px 0; overflow: hidden;">
                <div id="equityProcessingProgress" style="background: #3b82f6; height: 100%; width: 50%; transition: width 0.5s ease;"></div>
              </div>
              <div style="color: #6b7280; font-size: 14px;">
                This process takes about 2 minutes
              </div>
            </div>
          </div>
        </div>
      `;

      const processingDiv = document.createElement('div');
      processingDiv.id = 'equityProcessingModal';
      processingDiv.innerHTML = processingHtml;
      document.body.appendChild(processingDiv);
      
      processingModal = processingDiv;
      processingModalRef.current = processingDiv;
    }

    // Show modal
    (processingModal as HTMLElement).style.display = 'flex';

    // Simulate progress
    setTimeout(() => {
      if (!mountedRef.current) return;
      const stepEl = document.getElementById('equityProcessingStep');
      const progressEl = document.getElementById('equityProcessingProgress');
      if (stepEl) stepEl.textContent = 'Finalizing changes...';
      if (progressEl) progressEl.style.width = '100%';
    }, 1000);
  }, [mountedRef]);

  const showSuccessCountdown = useCallback((onRedirect: () => void) => {
    if (!mountedRef.current) return;

    let countdown = 3;

    const updateCountdown = () => {
      if (!mountedRef.current) return;

      const successHtml = `
        <div style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10002; padding: 16px;">
          <div style="background: white; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); max-width: 500px; width: 100%; overflow: hidden;">
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgb(226, 232, 240); padding: 10px 12px;">
              <div>
                <h3 style="font-size: 16px; font-weight: 600; color: #059669; margin: 0;">✅ Equity Method Change Completed</h3>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: rgb(100, 116, 139);">Successfully updated</p>
              </div>
            </div>
            <div style="padding: 32px 24px; text-align: center;">
              <div style="font-size: 16px; margin-bottom: 20px; color: #059669; font-weight: 500;">
                Equity method successfully changed!
              </div>
              <div style="margin: 20px 0; color: #374151; font-size: 15px;">
                Redirecting to filings page in <strong id="equityCountdown" style="color: #3b82f6;">${countdown}</strong> seconds...
              </div>
            </div>
            <div class="flex items-center justify-center gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
              <button type="button" id="equityGoNow" class="inline-flex items-center justify-center rounded bg-slate-900 px-4 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400">
                Go Now
              </button>
            </div>
          </div>
        </div>
      `;

      const modal = processingModalRef.current;
      if (modal) {
        modal.innerHTML = successHtml;
        
        // Attach event listener
        requestAnimationFrame(() => {
          if (!mountedRef.current) return;
          
          const goNowBtn = document.getElementById('equityGoNow');
          if (goNowBtn) {
            addTrackedListener(goNowBtn, 'click', () => {
              if (processingModalRef.current) {
                processingModalRef.current.style.display = 'none';
              }
              onRedirect();
            });
          }
        });
      }

      if (countdown > 0) {
        setTimeout(() => {
          countdown--;
          updateCountdown();
        }, 1000);
      } else {
        if (processingModalRef.current) {
          processingModalRef.current.style.display = 'none';
        }
        onRedirect();
      }
    };

    updateCountdown();
  }, [mountedRef, addTrackedListener]);

  const proceedWithMethodChange = useCallback(async (newMethod: string) => {
    const ctx = contextRef.current;
    if (!ctx || !mountedRef.current) return;

    showProcessingModal();

    try {
      const { data, error } = await supabase.functions.invoke('trigger-equity-method-change', {
        body: {
          filing_id: ctx.filingId,
          org_id: ctx.orgId,
          old_method: ctx.currentMethod,
          new_method: newMethod
        }
      });

      if (!mountedRef.current) return;

      if (error) {
        if (processingModalRef.current) {
          processingModalRef.current.style.display = 'none';
        }
        alert(`Error: ${error.message || 'Failed to trigger equity method change'}`);
        return;
      }

      if (data?.status === 'success') {
        showSuccessCountdown(ctx.onSuccess);
      } else {
        if (processingModalRef.current) {
          processingModalRef.current.style.display = 'none';
        }
        alert(`Error: ${data?.error || 'Unknown error occurred'}`);
      }
    } catch (error: any) {
      if (!mountedRef.current) return;
      
      if (processingModalRef.current) {
        processingModalRef.current.style.display = 'none';
      }
      alert(`Network error: ${error.message}`);
    }
  }, [showProcessingModal, showSuccessCountdown, mountedRef]);

  const handleConfirm = useCallback(() => {
    const newMethod = newMethodSelectRef.current?.value;
    
    if (!newMethod) {
      alert('Please select a new method.');
      return;
    }

    close();
    showWarningModal(newMethod);
  }, [showWarningModal]);

  const open = useCallback(() => {
    if (!mountedRef.current) return;

    const ctx = getEquityMethodChangeModalContext();
    if (!ctx) return;

    contextRef.current = ctx;

    if (currentMethodDisplayRef.current) {
      currentMethodDisplayRef.current.textContent = ctx.currentMethod;
    }

    if (newMethodSelectRef.current) {
      newMethodSelectRef.current.innerHTML = `
        <option value="">Please Select</option>
        <option value="Statement of Changes In Equity">Statement of Changes In Equity</option>
        <option value="Statement of Retained Earnings">Statement of Retained Earnings</option>
      `;
    }

    if (modalRootRef.current) {
      modalRootRef.current.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }
  }, [mountedRef]);

  const close = useCallback(() => {
    if (!mountedRef.current) return;

    if (modalRootRef.current) {
      modalRootRef.current.style.display = 'none';
      document.body.style.overflow = '';
    }
  }, [mountedRef]);

  useEffect(() => {
    document.addEventListener(EQUITY_METHOD_CHANGE_MODAL_OPEN_EVENT, open);
    document.addEventListener(EQUITY_METHOD_CHANGE_MODAL_CLOSE_EVENT, close);

    return () => {
      // Cleanup on unmount
      mountedRef.current = false;
      
      document.removeEventListener(EQUITY_METHOD_CHANGE_MODAL_OPEN_EVENT, open);
      document.removeEventListener(EQUITY_METHOD_CHANGE_MODAL_CLOSE_EVENT, close);
      
      // Clean up all tracked event listeners
      cleanupListeners();
      
      // Remove secondary modals from DOM
      if (warningModalRef.current) {
        warningModalRef.current.remove();
        warningModalRef.current = null;
      }
      if (processingModalRef.current) {
        processingModalRef.current.remove();
        processingModalRef.current = null;
      }
      
      // Reset body overflow
      document.body.style.overflow = '';
    };
  }, [open, close, cleanupListeners]);

  return (
    <div
      ref={modalRootRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
    >
      <div
        ref={modalBackdropRef}
        onClick={closeEquityMethodChangeModal}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)'
        }}
      />

      <div
        ref={modalPanelRef}
        style={{
          position: 'relative',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
          <div>
            <h3 className="text-base font-semibold text-[var(--theme-400)]" style={{ margin: 0 }}>
              Equity Method Change
            </h3>
            <p className="mt-0.5 text-xs text-slate-500" style={{ margin: '4px 0 0 0' }}>
              Select a new method for Changes in Equity
            </p>
          </div>
          <button
            type="button"
            onClick={closeEquityMethodChangeModal}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
            aria-label="Close"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        <div style={{ padding: '24px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Current Equity Method
              </label>
              <div 
                ref={currentMethodDisplayRef}
                style={{
                  padding: '10px 12px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#6b7280'
                }}
              >
                -
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                New Equity Method <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                ref={newMethodSelectRef}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#111827'
                }}
              >
                <option value="">Please Select</option>
                <option value="Statement of Changes In Equity">Statement of Changes In Equity</option>
                <option value="Statement of Retained Earnings">Statement of Retained Earnings</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
          <button
            type="button"
            onClick={closeEquityMethodChangeModal}
            className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="inline-flex items-center justify-center rounded bg-slate-900 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};
